/**
 * O parser do "colar lista" da transferência: texto colado → linhas com
 * código, quantidade e, opcionalmente, os localizadores da linha. Separado do
 * componente para ser testável sozinho.
 *
 *   código                      → 1 un, localizadores do cabeçalho
 *   código qtde                 → localizadores do cabeçalho (o formato antigo)
 *   código qtde origem          → origem da linha, destino do cabeçalho
 *   código qtde origem destino  → os dois da linha
 *
 * Com TAB (Excel) ou `;` a célula vazia conta: `12745;3;;56307` usa a origem
 * do cabeçalho e o destino da linha.
 */

export interface LinhaLida {
  linha: number;
  texto: string;
  codigo: string | null;
  quantidade: number | null;
  /** Código do localizador da 3ª/4ª coluna. `null` = usar o padrão do cabeçalho. */
  origem: string | null;
  destino: string | null;
  /** Por que a linha não serve, quando não serve. */
  problema: string | null;
  cabecalho: boolean;
}

export const MAX_LINHAS = 2000;

/** O modelo para baixar: as quatro colunas, com e sem localizador próprio. */
export const MODELO_LISTA =
  'codigo;quantidade;localizador_origem;localizador_destino\r\n' +
  '12745;3;56302;56307\r\n' +
  '7897016833776;10;;\r\n';

/**
 * UMA regra só, igual para código interno e EAN: o código, um separador
 * QUALQUER, a quantidade.
 *
 * O separador é qualquer coisa que não seja dígito — TAB (célula copiada do
 * Excel), espaço, `;`, `,`, `.`, `-`, `|`, `x`. Antes ele dependia do que
 * havia na linha (TAB, senão `;`, senão `,`, senão espaço) e `12745.3` não
 * passava; quem colava não tinha como adivinhar a regra.
 */
const LINHA = /^"?(\d+)"?(?:[^\d]+?"?([\d.,]*\d)"?)?(.*)$/;

/**
 * A quantidade como a planilha em português devolve: "2,00", e "1.000" — que
 * é MIL, não 1. Ponto seguido de grupos de 3 dígitos é sempre milhar;
 * "12.0"/"12.00" é decimal zerado. Fração é recusada.
 */
export function quantidadeDe(q: string): number {
  if (/^\d{1,3}(\.\d{3})+(,0+)?$/.test(q)) {
    return Number(q.replace(/,0+$/, '').replace(/\./g, ''));
  }
  if (/^\d+(,0+|\.0{1,2})?$/.test(q)) return Number(q.split(/[.,]/)[0]);
  return NaN;
}

export function lerTexto(texto: string): LinhaLida[] {
  const lidas: LinhaLida[] = [];
  let primeira = true;
  texto.split(/\r?\n/).forEach((bruta, i) => {
    const t = bruta.trim();
    if (t === '') return;
    const ehPrimeira = primeira;
    primeira = false;
    const base = { linha: i + 1, texto: t, origem: null, destino: null };
    const falha = (codigo: string | null, problema: string): void => {
      lidas.push({ ...base, codigo, quantidade: null, problema, cabecalho: false });
    };
    const localizadorRuim = (rotulo: string, v: string | null): boolean => {
      if (v === null || /^\d{1,15}$/.test(v)) return false;
      falha(
        base.texto.match(/^"?(\d+)/)?.[1] ?? null,
        `${rotulo} "${v}" não é código de localizador`,
      );
      return true;
    };

    // O Excel transforma EAN em 7,89123E+12 quando a coluna não é texto — e
    // aí os últimos dígitos já se perderam. Não há o que recuperar.
    const cientifico = /^"?(\d+[.,]\d+e\+?\d+)/i.exec(t);
    if (cientifico) {
      falha(cientifico[1]!, 'EAN em notação científica — formate a coluna como texto no Excel');
      return;
    }

    /*
     * TAB (célula do Excel) ou `;`: é planilha, e a POSIÇÃO da célula diz o
     * que ela é — inclusive a vazia, que é "usa o padrão do cabeçalho".
     */
    if (/[\t;]/.test(t)) {
      const c = t.split(/[\t;]/).map((x) => x.trim().replace(/^"|"$/g, '').trim());
      while (c.length > 1 && c[c.length - 1] === '') c.pop();
      const codigo = c[0] ?? '';
      if (!/^\d+$/.test(codigo)) {
        if (ehPrimeira) {
          lidas.push({ ...base, codigo: null, quantidade: null, problema: null, cabecalho: true });
        } else {
          falha(null, 'a linha não começa com um código');
        }
        return;
      }
      if (codigo.length > 20) {
        falha(codigo, 'código longo demais');
        return;
      }
      if (c.length > 4) {
        falha(codigo, `${c.length} colunas — use código, qtde, origem, destino`);
        return;
      }
      const origem = c[2] || null;
      const destino = c[3] || null;
      if (localizadorRuim('origem', origem) || localizadorRuim('destino', destino)) return;
      const q = c[1] ?? '';
      const quantidade = q === '' ? 1 : quantidadeDe(q);
      if (!Number.isInteger(quantidade) || quantidade <= 0) {
        falha(codigo, `quantidade inválida (${q})`);
        return;
      }
      lidas.push({ ...base, codigo, quantidade, origem, destino, problema: null, cabecalho: false });
      return;
    }

    const m = LINHA.exec(t);
    if (!m) {
      // Primeira linha sem código na frente é o cabeçalho da planilha.
      if (ehPrimeira) {
        lidas.push({ ...base, codigo: null, quantidade: null, problema: null, cabecalho: true });
      } else {
        falha(null, 'a linha não começa com um código');
      }
      return;
    }

    const codigo = m[1]!;
    const qtde = m[2];
    const resto = m[3] ?? '';
    if (codigo.length > 20) {
      falha(codigo, 'código longo demais');
      return;
    }
    /*
     * Números depois da quantidade: os localizadores de origem e destino,
     * separados por espaço (ou `|`). Vírgula e ponto não servem aqui — eles
     * podem ser da quantidade ("1.000", "2,00").
     */
    let origem: string | null = null;
    let destino: string | null = null;
    if (/\d/.test(resto)) {
      const partes = resto
        .split(/[\s|]+/)
        .map((x) => x.replace(/^"|"$/g, ''))
        .filter((x) => x !== '');
      if (qtde === undefined || partes.length > 2 || !partes.every((x) => /^\d{1,15}$/.test(x))) {
        falha(
          codigo,
          'depois da quantidade só cabem os códigos do localizador de origem e de destino',
        );
        return;
      }
      origem = partes[0] ?? null;
      destino = partes[1] ?? null;
    }
    if (qtde === undefined && resto.trim() !== '') {
      falha(codigo, 'quantidade não é número');
      return;
    }

    // Só o código, sem quantidade: conta 1 (leitor bipando peça a peça).
    const quantidade = qtde === undefined ? 1 : quantidadeDe(qtde);
    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      falha(codigo, `quantidade inválida (${qtde})`);
      return;
    }
    lidas.push({ ...base, codigo, quantidade, origem, destino, problema: null, cabecalho: false });
  });
  return lidas;
}
